import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import CarCheckoutTemplate from "../../sections/pdp-checkout-template";
import { AccordionWrapper, Heading, PrimaryLink } from "../../elements";
import HeroMedia from "../HeroMedia";
import PersonalInformationAccordion from "../../sections/personal-information-accordion";
import DeliveryDetailsAccordion from "../../sections/delivery-details-accordion";
import useQuery from "../../utils/useQuery";
import { getOrderDetail } from "../../services/orderService";
import { clearPdpAction } from "../../redux/actions/pdp";
import { format } from "../../utils";
import getSiteInfo from "../../utils/getSiteInfo";
import { getMarketCountry } from "../../services/siteService";
import { generateReactKey } from "../../utils/reactKey";
import { useHistory } from "react-router-dom";
import { ProductType, orderTypes } from "../../utils/lynkco-constants";
import { getProductByOrderPreview } from "../../utils/measurements";
import PropTypes from "prop-types";

const PdpOrderConfirmation = ({ fields, rendering }) => {
  const query = useQuery();
  const history = useHistory();
  const dispatch = useDispatch();
  const clearPdp = () => dispatch(clearPdpAction());

  const { headingType, headingBody, linksHeadingType, linksHeadingBody, deliveryHeadingType, deliveryHeadingBody, isDeliveryOpen, links, heroMedia, customerNumberLabel, orderNumberLabel, orderDateLabel, cardUsedLabel, isPersonalInfoOpen, personalHeadingType, personalHeadingBody } = fields;

  const [orderDetails, setOrderDetails] = useState([]);
  const [orderDetailsApiRes, setOrderDetailsApiRes] = useState(null);
  const [deliveryDetails, setDeliveryDetails] = useState([]);
  const [showSsn, setShowSsn] = useState(false);

  const templateProps = {
    fields,
    className: "start-of-sales-order-confirmation",
    bodyClass: "start-of-sales-order-confirmation-body",
    orderDetail: orderDetailsApiRes,
    isNOCheckConfirmation: true
  };
  const personalInfoProps = {
    fields,
    shippingDetails: orderDetailsApiRes
      ? [`${orderDetailsApiRes?.shippingInfo.firstName || ""} ${orderDetailsApiRes?.shippingInfo.lastName || ""}`, `${(showSsn && orderDetailsApiRes?.orderExtraData.ssn) || ""}`, `${orderDetailsApiRes?.shippingInfo.mobileCountryCode || ""} ${orderDetailsApiRes?.shippingInfo.mobile || ""}`]
      : [],
    billingDetails: orderDetailsApiRes ? [`${orderDetailsApiRes?.billingInfo.address1 || ""} ${orderDetailsApiRes?.billingInfo.address2 || ""}`, `${orderDetailsApiRes?.billingInfo.postCode || ""} ${orderDetailsApiRes?.billingInfo.city || ""}`, `${orderDetailsApiRes?.billingInfo.country || ""}`] : []
  };
  const personalInfoAccordionProps = {
    heading: {
      type: personalHeadingType,
      html: personalHeadingBody,
      typographyClass: "type-link"
    },
    open: isPersonalInfoOpen.value
  };
  const deliveryAccordionProps = {
    heading: {
      type: deliveryHeadingType,
      html: deliveryHeadingBody,
      typographyClass: "type-link"
    },
    open: isDeliveryOpen.value
  };

  const deliveryProps = {
    fields,
    details: orderDetailsApiRes
      ? [
          `${orderDetailsApiRes?.orderExtraData.touchPointName || ""} ` || `${orderDetailsApiRes?.shippingInfo.firstName || ""} ${orderDetailsApiRes?.shippingInfo.lastName || ""}`,
          `${orderDetailsApiRes?.shippingInfo.address1 || ""} ${orderDetailsApiRes?.shippingInfo.address2 || ""}`,
          `${orderDetailsApiRes?.shippingInfo.postCode || ""} ${orderDetailsApiRes?.shippingInfo.city || ""}`
        ]
      : []
  };

  const [heroMediaProps, setHeroMediaProps] = useState({
    fields: {
      ...heroMedia.fields
    },
    rendering
  });

  const getCountryFromMarket = async () => {
    const country = await getMarketCountry();
    if ((country.regionAbbr === "SE" || country.regionAbbr === "IT" || country.regionAbbr === "ES") && orderDetailsApiRes?.subType === orderTypes?.monthToMonth) {
      setShowSsn(true);
    } else {
      setShowSsn(false);
    }
    return showSsn;
  };
  useEffect(() => {
    getCountryFromMarket();
  }, [orderDetailsApiRes]);

  const redirectToPdp = () => {
    history.replace(templateProps?.fields?.template?.fields?.pdpLink?.value?.href);
  };

  const getOrderDetails = async () => {
    const siteInfo = await getSiteInfo();
    if (!query?.orderId) {
      redirectToPdp();
      return;
    }
    let res = await getOrderDetail(query?.orderId);

    /** Measurement */
    let dataLayer = window.dataLayer || [];

    if (!res) {
      redirectToPdp();
      return;
    } else {
      const checkoutFlow = checkoutFlowMapping(res);
      dataLayer.push({
        event: "checkoutOption",
        option: res?.payMethod,
        step: 3,
        checkoutFlow: checkoutFlow
      });

      let transaction_details = {
        id: res?.orderNo,
        affiliation: window?.location?.href,
        revenue: res?.orderExtraData?.totalAmount || res?.orderExtraData?.loanTotalAmount,
        tax: res?.vat,
        shipping: res?.deliveryFee || res.shipping || "",
        coupon: ""
      };

      let product = getProductByOrderPreview(res);

      dataLayer.push({
        event: "purchase",
        checkoutFlow: product?.membershipPlan,
        transaction: transaction_details,
        products: [product]
      });
    }

    clearPdp();
    setOrderDetailsApiRes(res);
    const values = res;
    const shippingInfo = res.shippingInfo;
    const productImage = res.suborders[0]?.orderItems.filter(x => x.productType === ProductType.vehicle)[0]?.image;
    setHeroMediaProps({
      fields: {
        ...heroMedia.fields,
        image: {
          value: {
            src: productImage
          }
        }
      },
      rendering
    });
    setOrderDetails([
      {
        label: customerNumberLabel.value,
        value: values.customerNumber
      },
      {
        label: orderNumberLabel.value,
        value: values.orderNo
      },
      {
        label: orderDateLabel.value,
        value: format(values.updateTime, siteInfo.dateFormat)
      },
      {
        label: cardUsedLabel.value,
        value: values.cardNo
      }
    ]);
    setDeliveryDetails([`${shippingInfo.fullName || ""}`, `${shippingInfo.address1 || ""} ${shippingInfo.address2 || ""}`, `${shippingInfo?.postCode || ""} ${shippingInfo.city || ""}`]);
  };

  const checkoutFlowMapping = order => {
    let checkoutFlow;
    switch (order.subType) {
      case "ORDERSUBTYPE03":
        checkoutFlow = "month-to-month";
        break;
      case "ORDERSUBTYPE04":
        checkoutFlow = "long-term";
        break;
      case "ORDERSUBTYPE05":
        checkoutFlow = "buy-car-loan";
        break;
      case "ORDERSUBTYPE06":
        checkoutFlow = "buy-car-cash";
        break;
      default:
        checkoutFlow = "";
    }
    return checkoutFlow;
  };

  useEffect(() => {
    getOrderDetails();
  }, []);

  return (
    <CarCheckoutTemplate {...templateProps}>
      <div className="start-of-sales-hero">
        <HeroMedia {...heroMediaProps} />
      </div>
      <div className="order-detals">
        <div className="order-details-heading">
          <Heading type={headingType} html={headingBody} typographyClass="type-h4" />
        </div>
        {orderDetails?.map(item => {
          return (
            <div className="summary-row" key={generateReactKey()}>
              <div className="label">{item.label}</div>
              <div className="value">{item.value}</div>
            </div>
          );
        })}
      </div>
      <div className="additional-details">
        <div className="additional-details-heading">
          <Heading type={linksHeadingType} html={linksHeadingBody} />
        </div>
        {links?.map((link, index) => {
          return <PrimaryLink {...link.fields} key={generateReactKey()} />;
        })}
      </div>
      <div className="start-of-sales-delivery-details">
        <AccordionWrapper {...personalInfoAccordionProps}>
          <PersonalInformationAccordion {...personalInfoProps} />
        </AccordionWrapper>
        <AccordionWrapper {...deliveryAccordionProps}>
          <DeliveryDetailsAccordion fields={fields} details={deliveryDetails} deliveryLabel={orderDetailsApiRes?.orderExtraData?.carDeliveryMethod} {...deliveryProps} />
        </AccordionWrapper>
      </div>
    </CarCheckoutTemplate>
  );
};
export default PdpOrderConfirmation;

PdpOrderConfirmation.propTypes = {
  fields: PropTypes.shape({
    headingType: PropTypes.object,
    headingBody: PropTypes.shape({ value: PropTypes.string }),
    linksHeadingType: PropTypes.object,
    linksHeadingBody: PropTypes.shape({ value: PropTypes.string }),
    deliveryHeadingType: PropTypes.object,
    deliveryHeadingBody: PropTypes.shape({ value: PropTypes.string }),
    isDeliveryOpen: PropTypes.shape({ value: PropTypes.bool }),
    links: PropTypes.array,
    heroMedia: PropTypes.object,
    customerNumberLabel: PropTypes.shape({ value: PropTypes.string }),
    orderNumberLabel: PropTypes.shape({ value: PropTypes.string }),
    orderDateLabel: PropTypes.shape({ value: PropTypes.string }),
    cardUsedLabel: PropTypes.shape({ value: PropTypes.string }),
    isPersonalInfoOpen: PropTypes.shape({ value: PropTypes.bool }),
    personalHeadingType: PropTypes.object,
    personalHeadingBody: PropTypes.shape({ value: PropTypes.string })
  }),
  rendering: PropTypes.shape({ dataSource: PropTypes.string })
};
